/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
  RendererStyleFlags2,
  Signal,
  ViewChild,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { OSKAuthService } from 'src/app/features/authentication/services/auth.service';
import { OSKTranslatePipe } from '@oskey/translate';
import { OSKCurrentUserToken } from '@oskey/core';
import { OSKSidemenuService } from './services/sidemenu/sidemenu.service';
import { OSKCurrentSidemenus } from './types/current-side-menu.type';
import { OSKCurrentUser, OSKUserAccount } from '@oskey/core/types';
import {
  OSKAvatarComponent,
  OSKBadgeComponent,
  OSKButtonComponent,
  OSKDividerComponent,
  OSKIconComponent
} from '@oskey/ui';
import { Subscription } from 'rxjs';

/**
 * The component responsible for showing the side menu.
 *
 * It tracks the changes in the sidemenu signal from the {@link OSKSidemenuService} and displays the current menu
 */
@Component({
  standalone: true,
  imports: [
    RouterModule,
    OSKTranslatePipe,
    OSKAvatarComponent,
    OSKBadgeComponent,
    OSKButtonComponent,
    OSKDividerComponent,
    OSKIconComponent
  ],
  selector: 'osk-sidemenu',
  templateUrl: './sidemenu.component.html',
  styleUrl: './sidemenu.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('350ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('350ms', style({ opacity: 0 }))])
    ]),
    trigger('rotateInOut', [
      transition(':enter', [
        animate(
          '350ms',
          keyframes([style({ transform: 'rotate(0deg)' }), style({ transform: 'rotate(90deg)' })])
        )
      ]),
      transition(':leave', [
        animate(
          '300ms',
          keyframes([style({ transform: 'rotate(0deg)' }), style({ transform: 'rotate(-90deg)' })])
        )
      ])
    ])
  ]
})
export class OSKSidemenuComponent implements OnDestroy {
  private readonly observer: BreakpointObserver = inject(BreakpointObserver);
  private readonly router: Router = inject(Router);
  private readonly authService: OSKAuthService = inject(OSKAuthService);
  private readonly sidemenuService: OSKSidemenuService = inject(OSKSidemenuService);
  protected readonly sidemenu: Signal<OSKCurrentSidemenus> = this.sidemenuService.sidemenu;
  protected readonly currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  private readonly document: Document = inject(DOCUMENT);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  protected isMobile: boolean = false;
  protected isCollapsed: boolean = true;
  protected isMultiple: boolean = false;
  protected routerUrl: string = '';
  protected accountMenuOpen: WritableSignal<boolean> = signal(false);
  protected submenuGroupOpen: WritableSignal<boolean> = signal(false);
  private routerSubscription: Subscription;

  @ViewChild('accountTrigger', { read: ElementRef })
  protected accountTrigger?: ElementRef<HTMLElement>;

  constructor() {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.routerUrl = event.urlAfterRedirects;
      }
    });

    this.routerUrl = this.router.url;
    this.observer
      .observe(['(max-width: 600px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((screenSize) => {
        this.isMobile = screenSize.matches;
        this.isCollapsed = screenSize.matches;
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.renderer.removeStyle(
      this.document.documentElement,
      '--user-account-menu-width',
      RendererStyleFlags2.DashCase
    );
  }

  protected updateMenuWidth(): void {
    if (this.accountTrigger && !this.isCollapsed) {
      const width = this.accountTrigger.nativeElement.offsetWidth;
      if (width > 0) {
        this.renderer.setStyle(
          this.document.documentElement,
          '--user-account-menu-width',
          `${width}px`,
          RendererStyleFlags2.DashCase
        );
        return;
      }
    }
    this.renderer.setStyle(
      this.document.documentElement,
      '--user-account-menu-width',
      '250px',
      RendererStyleFlags2.DashCase
    );
  }

  get userInitials(): string {
    const profile = this.currentUser().oskUser?.publicProfile;
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
    }
    if (profile?.firstName) {
      return profile.firstName.substring(0, 2).toUpperCase();
    }
    const name = this.userFullName;
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    const email = this.userEmail;
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'OS';
  }

  get userFullName(): string {
    const profile = this.currentUser().oskUser?.publicProfile;
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    if (profile?.firstName) {
      return profile.firstName;
    }
    return (
      this.currentUser().firebaseUser?.displayName ||
      this.currentUser().selectedAccount?.name ||
      ''
    );
  }

  get userEmail(): string {
    return this.currentUser().oskUser?.email || this.currentUser().firebaseUser?.email || '';
  }

  get accountsList(): OSKUserAccount[] {
    return this.currentUser().oskUser?.accounts || [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.accountMenuOpen() &&
      event.target instanceof Node &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.accountMenuOpen.set(false);
    }
  }

  toggleAccountMenu(event: Event): void {
    event.stopPropagation();
    const next = !this.accountMenuOpen();
    if (next) {
      this.updateMenuWidth();
    }
    this.accountMenuOpen.set(next);
  }

  closeAccountMenu(): void {
    this.accountMenuOpen.set(false);
  }

  toggleSubmenuGroup(): void {
    this.submenuGroupOpen.update((v) => !v);
  }

  toggleMenu(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  showPreviousMenu(): void {
    this.sidemenuService.showPreviousMenu();
  }

  showNextMenu(): void {
    this.sidemenuService.showNextMenu();
  }

  signOut(): void {
    this.accountMenuOpen.set(false);
    this.authService.signOut().then(() => {
      this.router.navigateByUrl('/');
    });
  }
}
